import {
    CenteredLayout,
    CenteredSpinner,
    FlexboxProps,
    FullWidthLayout,
    MaxWidthLayout,
} from '@luminovo/design-system';
import { Box, BoxProps } from '@mui/material';
import * as React from 'react';
import type { JSX } from 'react';

import { ErrorBoundaryClearedOnNavigation } from './errors/ErrorBoundaryClearedOnNavigation';

const scrollContainerId = 'scrollContainer';

const HeaderErrorFallback = (): JSX.Element => <Box />;

type Props = {
    header?: JSX.Element;
    layout?: keyof typeof containers;

    /**
     * By default, the PageLayout will insert a 60px padding at the bottom of the page.
     * This behaviour is not always ideal, so you can use this property to toggle it.
     */
    removeBottomPadding?: boolean;
} & BoxProps;

const containers: Record<string, React.ComponentType<BoxProps> | React.ComponentType<FlexboxProps>> = {
    'max-width': MaxWidthLayout,
    'full-width': FullWidthLayout,
    centered: CenteredLayout,
    default: Box,
    fragment: React.Fragment,
};

/**
 * Use this as the top component of your page.
 *
 * The app navbar is not part of it: the `_authenticated` layout renders that once, above the page.
 *
 * Pages are white, and their content starts 20 px from the edge. A list page gets that from `SidebarPageContent`
 * (8 px) and a `LayoutCard` (12 px); a page without a `LayoutCard` pads 20 px itself:
 *
 * ```
 * <PageLayout sx={{ paddingX: '20px', paddingTop: '16px' }}>
 * ```
 *
 * The `layout` presets pad 16 px (`full-width`) or 24 px (`max-width`), so they suit centred or nested content, not a
 * page's own inset.
 */
export function PageLayout({ header, layout = 'default', children, removeBottomPadding, ...rest }: Props) {
    const Container = containers[layout];

    return (
        <Box
            sx={{
                boxSizing: 'border-box',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            {/* Clears on navigation: a layout route's `PageLayout` stays mounted across its children (PCB tabs). */}
            {header && (
                <ErrorBoundaryClearedOnNavigation fallback={HeaderErrorFallback}>
                    <React.Suspense fallback={<Box />}>{header}</React.Suspense>
                </ErrorBoundaryClearedOnNavigation>
            )}
            <Box
                id={scrollContainerId}
                {...rest}
                sx={[
                    {
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        overflow: 'auto',
                        flexGrow: 1,
                        boxSizing: 'border-box',
                        padding: 0,
                        paddingBottom: removeBottomPadding ? undefined : '60px',
                    },
                    ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx]),
                ]}
            >
                <ErrorBoundaryClearedOnNavigation>
                    <React.Suspense fallback={<CenteredSpinner />}>
                        {layout === 'fragment' ? (
                            children
                        ) : (
                            <Container sx={{ minWidth: 'fit-content' }}>{children}</Container>
                        )}
                    </React.Suspense>
                </ErrorBoundaryClearedOnNavigation>
            </Box>
        </Box>
    );
}
